/**
 * TemplatePreviewModal.jsx - Template Preview Modal Component
 * 
 * Part 5 Checklist Items Covered:
 * - K1: Preview Overlay (dark backdrop)
 * - K2: Preview Card (content container)
 * - K3: Preview Header (template name + close)
 * - K4: Desktop/Mobile Toggle (preview mode switch)
 * - K5: Subject Display (email subject line)
 * - K6: HTML Rendered Preview (iframe/dangerouslySetInnerHTML)
 * - K7: Close Button (dismiss modal)
 */

import React, { useState } from 'react';
import { X, Monitor, Smartphone, Mail } from 'lucide-react';
import { COLORS } from './messagingStyles';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = {
  // K1: Preview Overlay
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 24,
  },
  
  // K2: Preview Card
  card: {
    width: '100%',
    maxWidth: 900,
    maxHeight: '90vh',
    borderRadius: 20,
    background: COLORS.surface,
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  
  // K3: Preview Header
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: `1px solid ${COLORS.borderLight}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: COLORS.goldLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  headerSubtitle: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginTop: 2,
  },
  
  // K4: Desktop/Mobile Toggle
  viewToggle: {
    display: 'flex',
    gap: 4,
    padding: 4,
    borderRadius: 10,
    background: COLORS.chipBg,
  },
  viewToggleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 36,
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  viewToggleBtnActive: {
    background: COLORS.gold,
  },
  
  // K7: Close Button
  closeBtn: {
    width: 40,
    height: 40,
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.15s',
    marginLeft: 12,
  },
  
  // Content area
  content: {
    flex: 1,
    padding: 24,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  
  // K5: Subject Display
  subjectBar: {
    width: '100%',
    maxWidth: 600,
    padding: '14px 18px',
    borderRadius: 12,
    background: COLORS.chipBg,
    marginBottom: 20,
  },
  subjectLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 4,
  },
  subjectText: {
    fontSize: 15,
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  
  // K6: HTML Rendered Preview
  previewContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  desktopFrame: {
    width: '100%',
    maxWidth: 600,
    minHeight: 400,
    borderRadius: 12,
    border: `1px solid ${COLORS.borderLight}`,
    background: '#FFFFFF',
    overflow: 'hidden',
  },
  mobileFrame: {
    width: 375,
    minHeight: 667,
    borderRadius: 24,
    border: '8px solid #1A1A1A',
    background: '#FFFFFF',
    overflow: 'hidden',
    boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
  },
  previewContent: {
    padding: 20,
    fontSize: 14,
    lineHeight: 1.6,
    color: '#333',
  },
  
  // Empty state
  emptyPreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 60,
    textAlign: 'center',
  },
  emptyIcon: {
    width: 56,
    height: 56,
    borderRadius: 14,
    background: COLORS.goldLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 8,
  },
  emptyBody: {
    fontSize: 13,
    color: COLORS.textMuted,
  },
};

// ─── Component ──────────────────────────────────────────────────────────────────

const TemplatePreviewModal = ({ template, onClose }) => {
  // K4: Preview mode state
  const [viewMode, setViewMode] = useState('desktop'); // 'desktop' | 'mobile'

  if (!template) return null;

  const hasContent = template.html_content || template.body;

  return (
    // K1: Preview Overlay
    <div style={styles.overlay} onClick={onClose} data-testid="template-preview-modal">
      {/* K2: Preview Card */}
      <div style={styles.card} onClick={e => e.stopPropagation()}>
        {/* K3: Preview Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.headerIcon}>
              <Mail size={20} color={COLORS.gold} />
            </div>
            <div>
              <div style={styles.headerTitle}>{template.name || 'Untitled Template'}</div>
              <div style={styles.headerSubtitle}>
                {template.type || 'Custom'} · {template.status || 'Draft'}
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* K4: Desktop/Mobile Toggle */}
            <div style={styles.viewToggle}>
              <button
                style={{
                  ...styles.viewToggleBtn,
                  ...(viewMode === 'desktop' ? styles.viewToggleBtnActive : {}),
                }}
                onClick={() => setViewMode('desktop')}
                data-testid="preview-desktop-btn"
              >
                <Monitor size={18} color={viewMode === 'desktop' ? '#FFFFFF' : COLORS.textMuted} />
              </button>
              <button
                style={{
                  ...styles.viewToggleBtn,
                  ...(viewMode === 'mobile' ? styles.viewToggleBtnActive : {}),
                }}
                onClick={() => setViewMode('mobile')}
                data-testid="preview-mobile-btn"
              >
                <Smartphone size={18} color={viewMode === 'mobile' ? '#FFFFFF' : COLORS.textMuted} />
              </button>
            </div>
            
            {/* K7: Close Button */}
            <button
              style={styles.closeBtn}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = COLORS.goldLight;
                e.currentTarget.style.borderColor = COLORS.gold;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = COLORS.surface;
                e.currentTarget.style.borderColor = COLORS.borderMedium;
              }}
              data-testid="preview-close-btn"
            >
              <X size={18} color={COLORS.textSecondary} />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div style={styles.content}>
          {/* K5: Subject Display */}
          {template.subject && (
            <div style={styles.subjectBar}>
              <div style={styles.subjectLabel}>Subject Line</div>
              <div style={styles.subjectText}>{template.subject}</div>
            </div>
          )}
          
          {/* K6: HTML Rendered Preview */}
          <div style={styles.previewContainer}>
            {hasContent ? (
              <div style={viewMode === 'mobile' ? styles.mobileFrame : styles.desktopFrame}>
                <div 
                  style={styles.previewContent}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(template.html_content || template.body || '')
                  }}
                />
              </div>
            ) : (
              <div style={viewMode === 'mobile' ? styles.mobileFrame : styles.desktopFrame}>
                <div style={styles.emptyPreview}>
                  <div style={styles.emptyIcon}>
                    <Mail size={24} color={COLORS.gold} />
                  </div>
                  <div style={styles.emptyTitle}>No Content</div>
                  <div style={styles.emptyBody}>
                    This template doesn't have any HTML content yet.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;
